<template>
    <div
        id="inner-app"
        class="flex flex-wrap h-screen"
        :class="{
            'overflow-hidden overflow-y-auto': !sidebarOpen,
            'overflow-hidden sm:overflow-y-auto': sidebarOpen,
        }"
    >
        <Menu />
        <div
            id="f-body"
            class="relative flex flex-col w-full h-full sm:pt-8 justify-start overflow-y-auto overflow-x-hidden bg-gray-100 dark:bg-slate-900 z-40 transition-all duration-300"
            :class="{
                'max-sm:translate-x-64 sm:ml-64 md:ml-64 overflow-y-hidden sm:overflow-y-auto':
                    sidebarOpen,
                'pt-20 sm:pt-24': appStore.hasBannerVisible,
            }"
        >
            <LoadingState :loading="routeLoading" />
            <Header />
            <Breadcrumb />
            <main class="pt-2 md:pt-10 lg:pt-0 pb-2 md:pb-4 grow">
                <div class="py-4 md:py-8 w-full">
                    <slot />
                </div>
            </main>
            <Footer />
        </div>
    </div>
    <AppNotifications />
    <AffiliateDisclosureModal></AffiliateDisclosureModal>
    <MyNextCardMethodologyModal></MyNextCardMethodologyModal>
    <TourTeaser
        v-if="canShowTourTeaser"
        :key="`${userData.uuid}-${userData.tour_dismissed}-${userData.tour_teaser_eligible}`"
    />
    <EditReward></EditReward>
    <DataSync @complete="dataSyncComplete"></DataSync>
    <PhoneInvalidModal
        @close="showPhoneInvalidModal = false"
        :show-modal="showPhoneInvalidModal"
    ></PhoneInvalidModal>
    <TourManager />
    <PasskeySetupPrompt />
    <TravelPlusModal />
    <DecisionModals />
    <SpendingPromoModal />
</template>

<script setup>
import { usePage, router } from '@inertiajs/vue3';
import { useAppStore } from '@/store/app';
import { usePointsStore } from '@/store/points';
import { useDashboardStore } from '@/store/dashboard';
import { storeToRefs } from 'pinia';
import { ref, onMounted, onBeforeUnmount, computed, watch } from 'vue';
import { notify } from 'notiwind';
import Header from '@/Partials/Header.vue';
import Footer from '@/Partials/Footer.vue';
import Menu from '@/Partials/Menu.vue';
import Breadcrumb from '@/components/ui/breadcrumb/breadcrumb.vue';
import AppNotifications from '@/components/app-notifications.vue';
import AffiliateDisclosureModal from '@/modals/affiliate-disclosure-modal.vue';
import MyNextCardMethodologyModal from '@/modals/my-next-card-methodology.vue';
import TourTeaser from '@/components/tour/TourTeaser.vue';
import EditReward from '@/components/mypoints/modals/edit-reward.vue';
import DataSync from '@/components/data-sync/data-sync.vue';
import PhoneInvalidModal from '@/modals/phone-invalid.vue';
import TourManager from '@/components/tour/TourManager.vue';
import PasskeySetupPrompt from '@/components/auth/passkey-setup-prompt.vue';
import TravelPlusModal from '@/components/popups/travel-plus-modal.vue';
import DecisionModals from '@/components/wallet/decisions/modals/DecisionModals.vue';
import SpendingPromoModal from '@/components/spending/SpendingPromoModal.vue';
import { useDecisionsModalStore } from '@/store/decisions-modal';
import * as Sentry from '@sentry/vue';
import LoadingState from '@/components/ui/loading-state/loading-state.vue';

const appStore = useAppStore();
const pointsStore = usePointsStore();
const dashboardStore = useDashboardStore();
const decisionsModalStore = useDecisionsModalStore();
const { sidebarOpen } = storeToRefs(appStore);
const page = usePage();
const userData = computed(() => page.props.userData || {});
const showPhoneInvalidModal = ref(false);

// Pre-initialize banner visibility from Inertia props to prevent layout shift
// This must happen during setup (not onMounted) so values are ready before child components render
appStore.initializeBannerVisibilityFromProps(page.props);
const hasShownInitialFlash = ref(false);
let assetVersionInterval = null;
const routeLoading = ref(false);

// Invitation banner state
const tourTeaserSkipped = computed(() => !!page.props.tour_teaser_skipped);
const tourDismissed = computed(() => {
    const u = userData.value || {};
    return (
        u.tour_dismissed === true ||
        u.tour_dismissed === 1 ||
        u.tour_dismissed === '1' ||
        u.tour_dismissed === 'dismissed'
    );
});
const tourEligible = computed(() => {
    const u = userData.value || {};
    return (
        u.tour_teaser_eligible === true ||
        u.tour_teaser_eligible === 1 ||
        u.tour_teaser_eligible === '1'
    );
});

const shouldShowTravelPlusStepOnly = computed(() => {
    const u = userData.value || {};
    const stepSeen = !!u.travel_plus_tour_step_seen;
    return !stepSeen && (tourDismissed.value || !tourEligible.value);
});

const canShowTourTeaser = computed(() => {
    const u = userData.value || {};

    if (tourTeaserSkipped.value) return false;
    if (tourDismissed.value) return false;

    return !!u && tourEligible.value;
});

watch(
    userData,
    (v) => {
        try {
            console.debug('[Main] userData changed', v);
        } catch (_) {}
    },
    { deep: true }
);

watch(
    () => page.props.flash,
    () => {
        // If the initial flash was just shown on mount, skip the first watcher run
        if (!hasShownInitialFlash.value) {
            hasShownInitialFlash.value = true;
            return;
        }
        showFlashToasts();
    },
    { deep: true }
);

const dataSyncComplete = () => {
    pointsStore.getRewards(true);
    dashboardStore.getNumberOfPoints();
};

const showFlashToasts = () => {
    try {
        const f = page.props && page.props.flash ? page.props.flash : {};
        if (f && (f.success || f.message)) {
            notify(
                {
                    group: 'success',
                    title: f.title || 'Success',
                    text: f.success || f.message,
                },
                4000
            );

            try {
                const t = (f.title || '').toString().toLowerCase();
                const s = (f.success || f.message || '')
                    .toString()
                    .toLowerCase();
                if (
                    t.includes('email confirmed') ||
                    (s.includes('email') && s.includes('confirm'))
                ) {
                    sessionStorage.removeItem('dismiss_email_banner');
                }
            } catch (_) {}
        }
        if (f && f.error) {
            notify(
                { group: 'error', title: f.title || 'Error', text: f.error },
                4000
            );
        }
    } catch (_) {}
};

const checkAssetVersion = () => {
    window.ApiService.apiCall('/version', 'get')
        .then((response) => {
            const assetVersion = response.data.version;
            const currentAssetVersion =
                localStorage.getItem('asset_version') || null;
            if (!currentAssetVersion) {
                localStorage.setItem('asset_version', assetVersion);
            }

            if (
                currentAssetVersion &&
                assetVersion !== currentAssetVersion &&
                assetVersion !== null
            ) {
                localStorage.setItem('asset_version', assetVersion);

                const notifiedVersion = window.$cookies.get('version_notified');
                if (notifiedVersion !== assetVersion) {
                    window.$cookies.set('version_notified', assetVersion, '1d');

                    notify(
                        {
                            group: 'version',
                            title: 'New version available',
                            text: 'Please reload this page now to update.',
                            url: page.url,
                        },
                        -1
                    );
                }
            }
        })
        .catch(() => {
            // A failed check (offline, rate limited) is retried by the next
            // interval; it must not surface as an unhandled rejection (MY10X-207).
        });
};

const decisionModalTypes = [
    'annual-fees',
    'min-spend',
    'expiring-benefits',
    'unmatched-cards',
    'welcome-bonus',
];

onMounted(() => {
    decisionsModalStore.setWalletId(userData.value.wallet_id);

    const params = new URLSearchParams(window.location.search);
    const action = params.get('action');
    if (action && decisionModalTypes.includes(action)) {
        decisionsModalStore.openModal(action);
        const url = new URL(window.location.href);
        url.searchParams.delete('action');
        window.history.replaceState({}, '', url.pathname + (url.search || ''));
    }

    appStore.setUserSettings(userData.value);

    const checkTime = 1000 * 60 * 5;
    assetVersionInterval = setInterval(checkAssetVersion, checkTime);

    let $issuingBank = document.getElementById('card-issuing-bank');
    if ($issuingBank) {
        $issuingBank.addEventListener('change', (el) => {
            if (el.target.value !== '') {
                appStore.setInstitution(el.target.value);
            }
        });
    }

    let $deletes = document.querySelectorAll('.delete-btn');
    $deletes.forEach((el) => {
        el.addEventListener('click', (e) => {
            let conf = confirm('Are you sure?');

            if (!conf) {
                e.preventDefault();
            }
            return conf;
        });
    });

    if (userData.value.phone_invalid && !userData.value.ignore_phone_invalid) {
        showPhoneInvalidModal.value = true;
    }

    if (!hasShownInitialFlash.value) {
        showFlashToasts();
        hasShownInitialFlash.value = true;
    }

    Sentry.setUser({
        email: userData.value.email,
        username: userData.value.name,
        custom_data: {
            uuid: userData.value.uuid,
        },
    });

    try {
        window.__routeLoader = {
            show: () => (routeLoading.value = true),
            hide: () => (routeLoading.value = false),
        };
    } catch (_) {}

    // Real-time email-block enforcement without reload
    const checkEmailBlock = () => {
        try {
            // If userData hasn't loaded yet (no email field), skip the check entirely
            if (!userData.value || !userData.value.email) {
                return;
            }
            const verified = !!userData.value.email_verified;
            // If user is verified, no need to check deadline at all
            if (verified) {
                return;
            }
            const sec = page.props?.security || {};
            const deadline = sec.email_deadline_at
                ? new Date(sec.email_deadline_at).getTime()
                : null;
            const now = Date.now();
            const isBlocked = !verified && deadline && now >= deadline;
            if (
                isBlocked &&
                !String(page.url || '').startsWith('/verify-block')
            ) {
                router.visit('/verify-block', { replace: true });
            }
        } catch (_) {}
    };
    // run now and every 60s
    checkEmailBlock();
    // Clear existing timer if it exists to prevent duplicates
    if (window.__mainEmailBlockTimer)
        clearInterval(window.__mainEmailBlockTimer);
    const t = setInterval(checkEmailBlock, 60000);
    // store for cleanup
    window.__mainEmailBlockTimer = t;
});

onBeforeUnmount(() => {
    clearInterval(assetVersionInterval);

    try {
        if (window.__mainEmailBlockTimer)
            clearInterval(window.__mainEmailBlockTimer);
    } catch (_) {}
});
</script>
